import APIRequest from '~/lib/api_request';

/**
 * Create a loadOptions function for AsyncSelect
 * @param {string} resource - API resource path
 * @param {Object} options - Configuration options
 * @param {number} options.perPage - Number of items per page (default: 50)
 * @param {Function} options.formatOption - Custom function to format each option (default: basic id/name mapping)
 * @param {Object} options.additionalParams - Additional parameters to send with each request
 * @returns {Function} loadOptions function for AsyncSelect
 */
export const createLoadOptionsFunction = (resource, {
  perPage = 10,
  formatOption = (item) => ({
    value: item.id,
    label: item.name,
    ...item,
  }),
  additionalParams = {},
} = {}) => async (search, prevOptions, { page }) => {
  try {
    const extraParams = typeof additionalParams === 'function'
      ? additionalParams(search)
      : additionalParams;

    const response = await APIRequest.get({
      resource: `/v1/options/${resource}`,
      data:     {
        s: {
          query: search || undefined,
        },
        page,
        per_page: perPage,
        ...extraParams,
      },
    });

    const items = response.body?.items || response.body || [];
    const hasMore = response.body?.has_more || false;

    return {
      options:    items.map(formatOption),
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  } catch (error) {
    console.error(`Error fetching options from ${resource}:`, error);
    return {
      options:    [],
      hasMore:    false,
      additional: { page },
    };
  }
};

/**
 * Fetch selected options by IDs
 * @param {string} resource - API resource path for selected options
 * @param {Array} ids - Array of IDs to fetch
 * @param {Function} formatOption - Custom function to format each option (default: basic id/name mapping)
 * @returns {Promise<Array>} Selected options
 */
export const fetchSelectedOptions = async (
  resource,
  ids,
  formatOption = (item) => ({
    value: item.id,
    label: item.name,
    ...item,
  }),
) => {
  if (!ids?.length) return [];

  try {
    const response = await APIRequest.get({
      resource: `/v1/options/${resource}/selected`,
      data:     {
        ids,
      },
    });

    return (response.body || []).map(formatOption);
  } catch (error) {
    console.error(`Error fetching selected options from ${resource}:`, error);
    return [];
  }
};
